import { each } from "lodash";
import { initMultiImageInputs, MiiCtrls, MiiCtrlsByName } from "../multi-image-input";
import { initMultiTextInputs, MtiCtrls, MtiCtrlsByName } from "../multi-text-input";
import { initMultiDocumentInputs, MdiCtrls, MdiCtrlsByName } from "../multi-document-input";
import { initEditSupplier } from "./edit-supplier";
import { initEditExpert } from "./edit-expert";
import { initEditProduct } from "./edit-product";
import { initEditPost } from "./edit-news-post";
import { initEditMyAccount } from "./edit-my-account";
import { initPostListDeleteButtons } from "./delete-post";

const getWysiwygContent: Function = window["iw"].getWysiwygContent;
const errorModal: Function = window["iw"].modal.errorModal;
const scrollToWithHeaderOffset: Function = window["iw"].layout.scrollToWithHeaderOffset;

declare const tinymce: any;
declare const jQuery: any;
const $ = jQuery;

type PasswordMatchFunc = () => boolean;
type PasswordStrongFunc = () => boolean;
type OldPasswordFilledFunc = () => boolean;

export function initSupplierAccountForms(): void
{
    const $main = $(`.hs-supplier-account-page .supplier-account-main`);
    if ($main.length === 0)
    {
        return;
    }
    initEditSupplier($main);
    initEditExpert($main);
    initEditProduct($main);
    initEditPost($main);
    initEditMyAccount($main);
    initPostListDeleteButtons($main);
}

function addOnChangeHandler($wrap, onChange: Function): void
{
    // Normal text inputs
    const $inputs = $wrap.find(`:input`);
    $inputs.on("change input", function (event, data) {
        onChange($(this), event, data);
    });

    // File-related inputs
    $wrap.find(`[data-multi-image-input]`).on("mii-change", () => {
        onChange($(this));
    });
    $wrap.find(`[data-multi-text-input]`).on("mti-change", () => {
        onChange($(this));
    });
    $wrap.find(`[data-multi-document-input]`).on("mdi-change", () => {
        onChange($(this));
    });

    const $wysiwygs = $wrap.find(`.wysiwyg-editor`);

    // WYSIWYG
    if ($wysiwygs.length > 0)
    {
        if (typeof tinymce === "undefined")
        {
            console.error("setupWarnBeforePageLeave called too early? - tinymce undefined");
        }
        else
        {
            $wysiwygs.each(function () {
                const $wrap = $(this);
                const tinymceId: string = $wrap.attr("data-editor-id");
                const editor = tinymce.editors[tinymceId];
                if (editor)
                {
                    editor.on("change", () => {
                        onChange($wrap, editor, tinymceId);
                    });
                }
                else
                {
                    console.warn(`Failed to find tinymce editor ${tinymceId}`);
                }
            });
        }
    }
}

// Returns cancel func
function setupWarnBeforePageLeave($mainInner): () => void
{
    let edited = false;

    addOnChangeHandler($mainInner, () => {
        edited = true;
    });

    window.onbeforeunload = function (event) {
        if (edited)
        {
            event.preventDefault();
            return `You have unsaved changes!`;
        }
    };

    return (): void => {
        edited = false;
    };
}

let windowLoaded = false;

$(window).one("load", function () {
    windowLoaded = true;
});

export function setupFormCommonBehaviour($mainInner, onFirstChange?: Function)
{
    const $textInputs = $mainInner.find(`textarea,input[type="text"],input[type="email"],input[type="number"],input[type="url"],input[type="password"]`);
    const $newPwd = $textInputs.filter(`[name="new_password"]`);
    const $selects = $mainInner.find(`select`);
    const $checkboxes = $mainInner.find(`input[type="checkbox"][name]`);
    const $wysiwygs = $mainInner.find(`[data-wysiwyg]`);
    const miiCtrls: MiiCtrlsByName = initMultiImageInputs($mainInner);
    const mtiCtrls: MtiCtrlsByName = initMultiTextInputs($mainInner);
    const mdiCtrls: MdiCtrlsByName = initMultiDocumentInputs($mainInner);

    let passwordIsStrong: () => boolean = () => true;
    let passwordsMatch: () => boolean = () => true;
    let oldPasswordPresent: () => boolean = () => true;

    // On success, returns object mapping input names to values.
    // On error, displays error modal & returns undefined.
    function gatherData(): any {
        const data: any = {};
        let errorMsg: string = undefined;
        let $firstError: any = undefined;
        $mainInner.find(`.input-error`).removeClass("show");

        // Text & select inputs
        $textInputs.add($selects).each(function () {
            const $inp = $(this);
            const required: boolean = $inp.is(`[required]`);
            const value: string = $.trim($inp.val() || "");
            if (!value && required)
            {
                const $error = $inp.closest(`.input-section`).find(`.input-required-error`).addClass("show");
                errorMsg = `A required field is not filled in`;
                if (!$firstError)
                {
                    $firstError = $error;
                }
            }
            else if (value && this.validity?.typeMismatch)
            {
                const $error = $inp.closest(`.input-section`).find(`.input-invalid-error`).addClass("show");
                errorMsg = `One or more of your inputs has an invalid value.`;
                if (!$firstError)
                {
                    $firstError = $error;
                }
            }
            const name: string = $inp.attr("name");
            if (!name)
            {
                throw new Error(`Text/select input has no name`);
            }
            data[name] = value;
        });

        if (!errorMsg)
        {
            if (!passwordIsStrong() || !passwordsMatch() || !oldPasswordPresent())
            {
                errorMsg = `There is a problem with your passwords.`;
                scrollToWithHeaderOffset($newPwd.closest(".input-section"));
                $firstError = undefined;
            }
        }

        if (errorMsg)
        {
            errorModal(errorMsg);
            if ($firstError)
            {
                $firstError.closest(".collapsible-section").addClass("open opened");
                const $section = $firstError.closest(".input-section");
                if ($section.length > 0)
                {
                    scrollToWithHeaderOffset($section);
                }
            }
            return undefined;
        }

        // Checkbox inputs
        $checkboxes.each(function () {
            const $inp = $(this);
            const name: string = $inp.attr("name");
            if (!name)
            {
                throw new Error(`Checkbox has no name`);
            }
            if (!$inp.prop("checked"))
            {
                return;
            }
            if (!data[name])
            {
                data[name] = [];
            }
            data[name].push($inp.val());
        });

        // WYSIWYG
        $wysiwygs.each(function () {
            const $wrap = $(this);
            const name: string = $wrap.attr("data-wysiwyg");
            if (!name)
            {
                throw new Error(`wysiwyg has no name`);
            }
            const value: string = getWysiwygContent($wrap) || "";
            data[name] = value;
        });

        // Multi Image Inputs
        each(miiCtrls, (ctrls: MiiCtrls, name: string) => {
            data[name] = {
                saved: ctrls.getDefaultItems(),
                new: ctrls.getFileItems()
            };
        });

        let mdiErrorMsg: string = undefined;

        // Multi Document Inputs
        each(mdiCtrls, (ctrls: MdiCtrls, name: string) => {
            mdiErrorMsg = ctrls.getErrorMessage();
            if (mdiErrorMsg) {
                scrollToWithHeaderOffset(ctrls.getWrap());
                return false;
            }
            data[name] = {
                saved: ctrls.getSavedItems(),
                new: ctrls.getNewItems()
            };
        });

        if (mdiErrorMsg) {
            errorModal(mdiErrorMsg);
            return undefined;
        }

        // Multi Text Inputs
        each(mtiCtrls, (ctrls: MtiCtrls, name: string) => {
            data[name] = ctrls.getValues(); // string[]
        });

        return data;
    }

    let cancelWarnBeforePageLeave: () => void = undefined;

    if ($newPwd.length > 0) {
        $newPwd.each(function () {
            const $newPwd = $(this);
            setupPasswordInputsShowHide($newPwd, $mainInner);
            passwordIsStrong = setupPasswordStrength($newPwd, parseInt($mainInner.attr(`[data-min-pwd-length]`), 10) || 10);
            passwordsMatch = setupPasswordConfirm($newPwd, $textInputs.filter(`[name="new_password_confirm"]`));
            oldPasswordPresent = setupOldPasswordCheck($newPwd, $textInputs.filter(`[name="old_password"]`));
        });
    }

    const onLoad = () => {
        cancelWarnBeforePageLeave = setupWarnBeforePageLeave($mainInner);
        
        if (onFirstChange)
        {
            addOnChangeHandler($mainInner, () => {
                if (onFirstChange)
                {
                    onFirstChange();
                    onFirstChange = undefined;
                }
            });
        }
    };
    
    if (windowLoaded)
    {
        onLoad();
    }
    else
    {
        $(window).one("load", onLoad);
    }

    return {
        gatherData,

        cancelWarnBeforePageLeave(): void {
            if (cancelWarnBeforePageLeave)
            {
                cancelWarnBeforePageLeave();
            }
            else
            {
                console.warn(`cancelWarnBeforePageLeave not ready`);
            }
        }
    };
}

function setupPasswordInputsShowHide($newPwd, $wrap): void
{
    const $showIfNewPassword = $wrap.find(`[data-show-if-new-password]`);
    $newPwd.on("input", function () {
        $newPwd.val() ? $showIfNewPassword.addClass("show") : $showIfNewPassword.removeClass("show");
    }).trigger("input");
}

function setupOldPasswordCheck($newPwd, $oldPwd): OldPasswordFilledFunc
{
    const $oldPwdWrap = $oldPwd.closest(".input-section");
    const $error = $oldPwdWrap.find(`.input-password-missing-error`);
    const check = (): boolean => {
        if (!$newPwd.val() || $oldPwd.val())
        {
            $error.hide();
			return true;
        }
        $error.show();
		return false;
    };
    $newPwd.add($oldPwd).on("input change", function (event) {
        check();
    });
    check();
    return check;
}

function setupPasswordConfirm($password, $passwordConfirm): PasswordMatchFunc
{
    const $passwordConfirmWrap = $passwordConfirm.closest(".input-section");
	const $error = $passwordConfirmWrap.find(`.input-password-mismatch-error`);
	let confirmInputChanged: boolean = false;
	const check = (): boolean => {
		if ($password.val() === $passwordConfirm.val())
		{
			$error.hide();
			return true;
		}
		$error.show();
		return false;
	};
	$passwordConfirm.on("change", () => {
		confirmInputChanged = true;
		check();
	}).on("input", () => {
		if (confirmInputChanged)
		{
			check();
		}
	});
	$password.on("input", () => {
		if (confirmInputChanged)
		{
			check();
		}
	});
	return check;
}

function setupPasswordStrength($password, minPwdLen: number): PasswordStrongFunc {
    const $passwordWrap = $password.closest(".input-section");
	const $error = $passwordWrap.find(`.input-weak-password-error`);
	let inputChanged: boolean = false;
	const hasSpecialChar = (val: string): boolean => !!val.match(/[^a-z0-9]/i);
    const hasNumber = (val: string): boolean => !!val.match(/\d/);
	const check = (): boolean => {
		const val: string = $password.val();
		if (!$password.is(`[required]`) && !val)
		{
			$error.hide();
			return true;
		}
		if (val.length >= minPwdLen && hasSpecialChar(val) && hasNumber(val))
		{
			$error.hide();
			return true;
		}
		$error.show();
		return false;
	};
	$password.on("change", () => {
		inputChanged = true;
		check();
	}).on("input", () => {
		if (inputChanged)
		{
			check();
		}
	});
	return check;
}
